import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { buildUrl, fetchJson } from "../shared/api";
import { getMajor, isVersionHigher, isReleaseCandidate } from "../shared/version";
import type { ModuleInfo, Version } from "../shared/types/api";
import { useTranslation } from "react-i18next";

type RepoMap = Record<string, ReturnType<typeof createEmptyRepo>>;

function createEmptyRepo(module: ModuleInfo) {
    return {
        ...module,
        uniqueKey: `${module.nameOfRepo}-${module.tableName}`,
        version: "",
        minVersionOfProduct: "",
        versions: [] as Version[],
    };
}

function ModulesPage() {
    const { t } = useTranslation();

    const [repoData, setRepoData] = useState<RepoMap>({});
    const [filterOldVersions] = useState<boolean>(true);
    const [nameFilter, setNameFilter] = useState<string>("");

    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            try {
                const [modules, versions] = await Promise.all([
                    fetchJson<ModuleInfo[]>(buildUrl("/module/all")),
                    fetchJson<Version[]>(buildUrl("/version/all")),
                ]);

                if (cancelled) return;

                const map: RepoMap = {};

                for (const mod of modules) {
                    if (!mod.nameOfRepo) continue;
                    const key = `${mod.nameOfRepo}-${mod.tableName}`;
                    map[key] = createEmptyRepo(mod);
                }

                const versionsByRepo: Record<string, Version[]> = {};
                for (const ver of versions) {
                    if (!ver.repository || !ver.tag || ver.tag.toLowerCase().includes("snapshot") || isReleaseCandidate(ver.tag)) continue;
                    (versionsByRepo[ver.repository] ??= []).push(ver);
                }

                for (const key in map) {
                    const repo = map[key];
                    const repoVersions = versionsByRepo[repo.nameOfRepo] || [];

                    for (const ver of repoVersions) {
                        if (filterOldVersions && repo.nameOfRepo !== "CzechIdMng") {
                            if (getMajor(ver.parentVersion) < 12) continue;
                        }

                        if (filterOldVersions && repo.nameOfRepo === "CzechIdMng") {
                            if (getMajor(ver.tag) < 12) continue;
                        }

                        let shouldUpdate = false;
                        const match = repo.tableName.match(/IdM\s+(\d+)\.x\.x/);
                        const tableMajor = match ? Number(match[1]) : null;

                        if (tableMajor !== null) {
                            shouldUpdate = getMajor(ver.tag) === tableMajor;
                        } else if (repo.nameOfRepo !== "CzechIdMng") {
                            shouldUpdate = true;
                        }

                        if (shouldUpdate && (!repo.version || isVersionHigher(ver.tag, repo.version))) {
                            repo.versions.push(ver);
                            repo.minVersionOfProduct = ver.parentVersion;
                            repo.version = ver.tag;
                        }
                    }
                }

                setRepoData(map);
            } catch (error) {
                if (!cancelled) console.error("Error fetching module/version data:", error);
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [filterOldVersions]);

    return (
        <>
            <div>
                <div className="summary-card">
                    <label className="filter-label">
                        <FontAwesomeIcon icon={faMagnifyingGlass} /> {t("modules.filterLabel")}
                    </label>
                    <input className="filter-input"
                        type="text"
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        placeholder={t("modules.filterPlaceholder")}
                        onFocus={(e) => {
                            e.target.style.borderColor = "#4caf50";
                            e.target.style.boxShadow = "0 0 0 2px rgba(76, 175, 80, 0.3)";
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = "#d1d5db";
                            e.target.style.boxShadow = "none";
                        }}
                    />
                </div>
            </div>

            <div className="table-wrapper">
                <table>
                    <thead>
                    <tr>
                        <th></th>
                        <th>{t("modules.table.moduleName")}</th>
                        <th>{t("modules.table.latestVersion")}</th>
                        <th>{t("modules.table.minProductVersion")}</th>
                        <th>{t("modules.table.gitlabRepo")}</th>
                        <th>{t("modules.table.releaseNotes")}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {Object.values(repoData)
                        .filter(
                            (repo) =>
                                nameFilter === "" ||
                                repo.nameOfRepo.toLowerCase().includes(nameFilter.toLowerCase()) ||
                                repo.tableName.toLowerCase().includes(nameFilter.toLowerCase())
                        )
                        .sort((a, b) => a.tableName.localeCompare(b.tableName))
                        .map((repo) => {
                            const moduleVersionParam = encodeURIComponent(repo.nameOfRepo + "_" + repo.version);
                            const releaseNotesUrl = `/release-note?module-version=${moduleVersionParam}`;

                            const major = repo.version ? repo.version.split(".")[0] : "";
                            const releaseNotesModuleUrl = `/moduledetail?repository=${encodeURIComponent(
                                repo.nameOfRepo
                            )}&major=${encodeURIComponent(major)}`;

                            return (
                                <tr key={repo.uniqueKey}>
                                    <td>
                                        <button className="icon-button"
                                            title={t("modules.actions.viewVersions")}
                                        >
                                            <Link to={releaseNotesModuleUrl}><FontAwesomeIcon icon={faMagnifyingGlass} /></Link>
                                        </button>
                                    </td>
                                    <td>{repo.tableName}</td>
                                    <td>
                                        {repo.version ? <Link to={releaseNotesUrl}>{repo.version}</Link> : ""}
                                    </td>
                                    <td>{repo.minVersionOfProduct}</td>
                                    <td>
                                        {repo.urlGithubGitlabRepo ? (
                                            <a href={repo.urlGithubGitlabRepo} target="_blank" rel="noreferrer">
                                                {t("modules.actions.repo")}
                                            </a>
                                        ) : (
                                            ""
                                        )}
                                    </td>
                                    <td>
                                        <Link to={releaseNotesModuleUrl}>{t("modules.actions.notes")}</Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}

export default ModulesPage;
